/**
 * A GeometryOverlay is a container for geometries that should be displayed in the viewer and interacted with.
 * @module GeometryOverlay
 */

import { mm, Viewer } from "@inovitas/infra3dsdk";

import { transformGeoJSON } from "shared/common/utils/general-utils";
import { Feature, Geometry } from "./viewer/externals/geojson";
import { IFeatureStyle } from "./viewer/interfaces/IFeatureStyle";

/**
 * Represents the configuration options for configuring the GeometryOverlay.
 */
export interface IGeometryOverlayOptions {
  /**
   * The FeatureStyle that the geometries of the GeometryOverlay should have.
   * @default
   * ```typescript
   * {
   *      rules: [
   *          {
   *              filter: "",
   *              symbolizer: "point",
   *              params: {
   *                  size: 8,
   *                  stroke: [1, 0, 0, 1],
   *              },
   *          },
   *          {
   *              filter: "",
   *              params: {
   *                  stroke: [1, 0, 0, 1],
   *                  "stroke-width": 1
   *              },
   *              symbolizer: "line"
   *          },
   *          {
   *              filter: "",
   *              params: {
   *                  fill: [0.8, 0.3, 0.4, 0.6]
   *              },
   *              symbolizer: "polygon"
   *          },
   *      ],
   *      stylename: "layerstyle"
   *  }
   * ```
   */
  style?: IFeatureStyle;

  /**
   * The highlight style that the geometries of the GeometryOverlay should have when they are selected.
   * @default
   * ```typescript
   * {
   *      stylename: "default_highlightstyle",
   *      rules: [
   *          {
   *              filter: "",
   *              symbolizer: "point",
   *              params: {
   *                  size: 10,
   *                  stroke: [1, 1, 0, 1],
   *              },
   *          },
   *          {
   *              filter: "",
   *              symbolizer: "line",
   *              params: {
   *                  "stroke-width": 3,
   *                  stroke: [1, 1, 0, 1],
   *              },
   *          },
   *          {
   *              filter: "",
   *              symbolizer: "polygon",
   *              params: {
   *                  fill: [1, 1, 0, 1],
   *              },
   *          },
   *      ]
   *}
   * ```
   */
  highlightstyle?: IFeatureStyle;

  /**
   * Snapability of the geometries for other capture tools.
   * @default 'true'
   */
  snappable?: boolean;

  /**
   * Visibility of the geometries.
   * @default 'true'
   */
  visible?: boolean;
}

export class GeometryOverlay {
  private _viewer: Viewer;

  private _style: mm.FeatureStyle;

  private _highlightStyle: mm.FeatureStyle;

  private _snappable: boolean;

  private _visible: boolean;

  private _featureCollection: mm.FeatureCollection;

  private _features: {
    infra3d_feature: mm.Feature;
    geojson_geometry: Geometry;
  }[] = [];

  constructor(viewer: Viewer, options?: IGeometryOverlayOptions) {
    this._viewer = viewer;

    const defaultStyle: IFeatureStyle = {
      rules: [
        {
          filter: "",
          symbolizer: "point",
          params: {
            size: 8,
            stroke: [0.05, 0.57, 0.82, 1],
          },
        },
        {
          filter: "",
          params: {
            stroke: [0.05, 0.57, 0.82, 1],
            "stroke-width": 1,
          },
          symbolizer: "line",
        },
        {
          filter: "",
          params: {
            fill: [0.05, 0.57, 0.82, 1],
          },
          symbolizer: "polygon",
        },
      ],
      stylename: "layerstyle",
    };

    const defaultHighlightStyle: IFeatureStyle = {
      stylename: "default_highlightstyle",
      rules: [
        {
          filter: "",
          symbolizer: "point",
          params: {
            size: 10,
            stroke: [1, 0.404, 0, 1],
          },
        },
        {
          filter: "",
          symbolizer: "line",
          params: {
            "stroke-width": 3,
            stroke: [1, 0.404, 0, 1],
          },
        },
        {
          filter: "",
          symbolizer: "polygon",
          params: {
            fill: [1, 0.404, 0, 1],
          },
        },
      ],
    };

    this._style = new mm.FeatureStyle(options?.style || defaultStyle);

    this._highlightStyle = new mm.FeatureStyle(
      options?.highlightstyle || defaultHighlightStyle
    );

    this._snappable =
      options?.snappable !== undefined ? options.snappable : true;
    this._visible = options?.visible !== undefined ? options.visible : true;

    this._featureCollection = this._viewer
      .getScene()
      .getFeatureHandler()
      .createFeatureCollection(
        this._style,
        this._highlightStyle,
        "API-GeometryOverlay",
        true,
        false
      );
  }

  /**
   * Sets a new style for the geomteries on the overlay.
   * @param style - the new style
   */
  public setStyle(style: IFeatureStyle): void {
    this._featureCollection.changeStyle(new mm.FeatureStyle(style));
  }

  /**
   * Sets a new highlight style (selected-style) for the geomteries on the overlay.
   * @param highlightstyle - the new highlightstyle
   */
  public setHighlightStyle(highlightstyle: IFeatureStyle): void {
    this._featureCollection.changeStyle(
      undefined,
      new mm.FeatureStyle(highlightstyle)
    );
  }

  /**
   * @returns Returns the current snapability of the geometries on the overlay.
   */
  public getSnappable(): boolean {
    return this._snappable;
  }

  /**
   * Sets the snapability of all geometrys on the overlay.
   * @param {boolean} snappable - true = the geometries are snapable for other tools, false = not snapable
   */
  public setSnappable(snappable: boolean): void {
    if (snappable) {
      this._viewer.getToolHandler().addSnapLayer(this._featureCollection);
    } else {
      this._viewer.getToolHandler().removeSnapLayer(this._featureCollection);
    }
  }

  /**
   * @returns Returns the current visibility of the geometries on the overlay.
   */
  public getVisible(): boolean {
    return this._visible;
  }

  /**
   * Show/Hides all geometrys on the overlay.
   * @param {boolean} visible - true = show, false = hide
   */
  public setVisible(visible: boolean): void {
    this._visible = visible;
    if (this._visible) {
      this._featureCollection.show();
    } else {
      this._featureCollection.hide();
    }
  }

  /**
   * Adds a geometry to the overlay and displays it.
   * @param {Geometry} geometry - GeoJSON-formatted geometry. Also multi*- geometry types are allowed. Geometries with only x and y coordinates are displayed relative to the camera height.
   * @param {boolean} selected - Hihghlights the geometry
   * @default 'false'
   */
  public addGeometry(geometry: Geometry, selected?: boolean): void {
    const geojson_feature: Feature = {
      type: "Feature",
      geometry: geometry,
      properties: {},
    };

    const feature_transformed = transformGeoJSON(
      geojson_feature,
      4326,
      this._viewer.epsg || this._viewer.getScene().getCurrentGeoframe().srid
    );
    const feature =
      this._featureCollection.addFeatureFromGeoJSON(feature_transformed);

    if (!this._visible) feature.hide();

    this._features.push({
      infra3d_feature: feature,
      geojson_geometry: geometry,
    });

    if (selected) {
      feature.highlightOn();
    }

    this._featureCollection.update();
  }

  /**
   * Removes a geometry from the overlay. Make sure that the object reference is the same as when the geometry was added.
   * @param {Geometry} geometry - The geometry object.
   */
  public removeGeometry(geometry: Geometry): void {
    const index = this._features.findIndex(
      (i) => i["geojson_geometry"] === geometry
    );
    if (index !== -1) {
      this._featureCollection.destroyFeature(
        this._features[index]["infra3d_feature"],
        true
      );
      this._features.splice(index, 1);
    } else {
      console.log("The provided geometry has not been found in the scene.");
    }
  }

  /**
   * Removes all geometries from the geometry overlay.
   */
  public clear(): void {
    for (const index in this._features) {
      this._featureCollection.destroyFeature(
        this._features[index]["infra3d_feature"],
        true
      );
    }
    this._features = [];
  }

  /**
   * Selects / Highlights a geometry. Make sure that the object reference is the same as when the geometry was added.
   * @param {Geometry} geometry - The geometry object.
   */
  public selectGeometry(geometry: Geometry): void {
    const index = this._features.findIndex(
      (i) => i["geojson_geometry"] === geometry
    );
    if (index !== -1) {
      this._features[index]["infra3d_feature"].highlightOn(true);
    } else {
      console.log("The provided geometry has not been found in the scene.");
    }
  }

  /**
   * Unselects / De-highlights a geometry.
   * @param {Geometry} geometry - The geometry object.
   */
  public unselectGeometry(geometry: Geometry): void {
    const index = this._features.findIndex(
      (i) => i["geojson_geometry"] === geometry
    );
    if (index !== -1) {
      this._features[index]["infra3d_feature"].highlightOff(true);
    } else {
      console.log("The provided geometry has not been found in the scene.");
    }
  }

  /**
   * @ignore
   */
  public getFeatureCollection(): mm.FeatureCollection {
    return this._featureCollection;
  }

  /**
   * @ignore
   */
  public getFeatures(): {
    infra3d_feature: mm.Feature;
    geojson_geometry: Geometry;
  }[] {
    return this._features;
  }

  /**
   * @ignore
   */
  public destroy(): void {
    this._featureCollection.destroy();
  }
}
